/* global __DEV__, $ */

import bog from 'bog';
import getConfig from 'next/config';

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled, { withTheme, createGlobalStyle } from 'styled-components';

import withErrorHandler from '../../utils/withErrorHandler';
import * as TradingViewUtils from '../../utils/tradingview';
import { getTradingViewLocale } from '../../utils/locales';
import ChartContainer, { CHART_HEIGHT } from '../../components/ChartContainer';
import LoadingShade from '../LoadingShade';

export const TRADINGVIEW_VIEW_NAME = 'TradingView';

const TV_CONTAINER_ID = 'tradingview_4bca8';
const TV_CANDLE_SERIES = 1;
const TV_LINE_SERIES = 2;
const TV_INTERVAL = 100; // For setting trading view script.
const TV_MAX_LOAD_CHECKS = 15;

const {
  publicRuntimeConfig: { lightDarkFadeTime },
} = getConfig();

const TradingChartInject = createGlobalStyle`
  .tv-loading-spinner,
  .ant-spin-container {
    flex: 1 1 auto;
    flex-basis: ${CHART_HEIGHT}px;
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
  }
  .tv-loading-spinner.hidden {
    display: none;
  }
`;

const TradingViewChartContainer = styled(ChartContainer)`
  clip-path: ${({ theme }) => theme.tradingViewClipPath || ''};
`;

const mapStateToProps = ({ info }) => ({ symbols: info.symbols });

export default
@withTheme
@withErrorHandler
@connect(mapStateToProps)
class TradingViewChartRenderer extends Component {
  static propTypes = {
    hidden: PropTypes.bool.isRequired,
    theme: PropTypes.object,
    locale: PropTypes.string,
    symbol: PropTypes.string.isRequired,
    symbols: PropTypes.array,
    selectedView: PropTypes.string,
    fullScreen: PropTypes.bool,
    interval: PropTypes.number,
    lineType: PropTypes.string,
    showIndicatorsDialog: PropTypes.bool,
  };

  static defaultProps = {
    theme: {},
    locale: 'en',
    symbols: [],
    selectedView: 'default',
    fullScreen: false,
    interval: 60,
    lineType: '',
    showIndicatorsDialog: false,
  };

  state = { loading: false };

  _tv_interval = null;
  _tv_symbol_timeout = null;
  _tv_state_timeout = null;

  componentDidMount() {
    if (!this._tv) {
      // Need to wait for setting the <script> tag for TradingView.
      let count = 0;
      this._tv_interval = setInterval(() => {
        if (window.TradingView) {
          this._resetTVInterval();
          this._init();
        } else if (++count > TV_MAX_LOAD_CHECKS) this._resetTVInterval(); // Give up
      }, TV_INTERVAL);
    }
  }

  componentDidUpdate({
    locale: prevLocale,
    symbol: prevSymbol,
    lineType: prevLineType,
    theme: prevTheme,
    showIndicatorsDialog: prevShowIndicatorsDialog,
    interval,
  }) {
    const {
      locale,
      symbol,
      interval: currentInterval,
      lineType,
      theme,
      selectedView,
      showIndicatorsDialog,
    } = this.props;
    this.lineType = lineType;
    if (window.TradingView) {
      const tvInterval = TradingViewUtils.translateResolutionForTV(currentInterval);

      if (this._tv) {
        // resolution changed?
        if (interval !== currentInterval && this._tv.activeChart().resolution() !== tvInterval) {
          this.setState({ loading: true });
          this._tv.activeChart().setResolution(tvInterval);
          // locale, symbol or theme changed?
        } else if (locale !== prevLocale || symbol !== prevSymbol || theme !== prevTheme) {
          // language changed?
          // symbol changed?
          // or theme changed?
          // trigger a re-init of tradingview.
          try {
            this._tv && this._tv.remove();
          } catch (err) {}
          this._tv = null;
        }

        // line type activated?
        if (lineType !== prevLineType) {
          this.setState({ loading: true });
          this.lineType = lineType;
          if (lineType === 'Line') {
            // line
            if (prevLineType !== 'Line') {
              // switch to Candle form Line
              this._loadStudies(lineType, symbol, tvInterval);
            }
            this._tv.applyOverrides({ 'mainSeriesProperties.style': TV_LINE_SERIES });
          } else {
            // candles
            if (prevLineType === 'Line') {
              // switch to Line form Candle
              this._loadStudies(lineType, symbol, tvInterval);
            }
            this._tv.applyOverrides({ 'mainSeriesProperties.style': TV_CANDLE_SERIES });
          }
        }

        // show indicators dialog?
        // "Indicator" button clicked?
        if (showIndicatorsDialog !== prevShowIndicatorsDialog) {
          this._showIndicatorsDialog();
        }
      }

      // init tradingview?
      if (selectedView === TRADINGVIEW_VIEW_NAME) this._init();
    }
  }

  componentWillUnmount() {
    this._tv && this._tv.remove();
    this._tv = null;
    clearTimeout(this._tv_symbol_timeout);
    clearTimeout(this._tv_state_timeout);
    this._tv_symbol_timeout = null;
    this._tv_state_timeout = null;
  }

  _init() {
    const {
      symbol,
      symbols,
      selectedView,
      locale,
      interval: currentInterval,
      lineType,
      theme,
    } = this.props;
    // init tradingview?
    if (!this._tv && selectedView === TRADINGVIEW_VIEW_NAME) {
      if (__DEV__) bog.info('Init TradingView chart.');
      const tvInterval = TradingViewUtils.translateResolutionForTV(currentInterval);
      this.setState({ loading: true }); // eslint-disable-line react/no-did-update-set-state
      this._tv = new window.TradingView.widget( // eslint-disable-line new-cap
        TradingViewUtils.generateTradingViewConfig({
          containerId: TV_CONTAINER_ID,
          symbol,
          symbols,
          interval: tvInterval,
          locale: getTradingViewLocale(locale),
          theme,
        })
      );
      const el = `#${TV_CONTAINER_ID}`;
      typeof $ !== 'undefined' && $(el).css('opacity', 0);

      this._tv.onChartReady(() => {
        this._loadStudies(lineType, symbol, tvInterval);
        this._tv_symbol_timeout = setTimeout(() => {
          this._tv.setSymbol(symbol, tvInterval);
        }, 0);
        this._tv.applyOverrides(theme.tradingViewOverrides || {});
        if (lineType === 'Line') {
          this._tv.applyOverrides({ 'mainSeriesProperties.style': TV_LINE_SERIES });
        }
        typeof $ !== 'undefined' && $(el).animate({ opacity: 1 }, lightDarkFadeTime / 2);
        this._tv
          .chart()
          .onDataLoaded()
          .subscribe(null, () => {
            this._tv_state_timeout = setTimeout(() => {
              this.setState({ loading: false });
            }, 0);
          });

        this._tv.subscribe('onAutoSaveNeeded', () => {
          this._tv.save(chartData => {
            localStorage['myTradingView' + this.props.theme.mode + this.lineType] = JSON.stringify(
              chartData
            );
          });
        });
      });
    }
  }

  _resetTVInterval() {
    clearInterval(this._tv_interval);
    this._tv_interval = null;
  }

  _loadStudies = (lineType, symbol, interval, mode = this.props.theme.mode) => {
    const strCofig = localStorage['myTradingView' + mode + lineType];
    if (strCofig) {
      const config = JSON.parse(strCofig);
      try {
        for (let i = 0; i < config.charts[0].panes.length; i++) {
          config.charts[0].panes[i].sources[0].state.symbol = symbol;
          config.charts[0].panes[i].sources[0].state.interval = interval;
        }
      } catch (ex) {
        if (__DEV__) bog.error(ex);
      }
      this._tv.load(config); // bug https://github.com/tradingview/charting_library/issues/3610
      this._tv.chart().executeActionById('chartReset');
    } else if (lineType !== 'Line') {
      this._candleDefault();
    } else if (lineType === 'Line') {
      this._lineDefault();
    }
  };
  _candleDefault = () => {
    const { theme } = this.props;
    this._tv.chart().createStudy('Moving Average', false, false, [7], null, {
      'Plot.linewidth': 1,
      'Plot.color': theme.tradingViewStyle.ma1,
    });
    this._tv.chart().createStudy('Moving Average', false, false, [25], null, {
      'Plot.linewidth': 1,
      'Plot.color': theme.tradingViewStyle.ma2,
    });
    this._tv.chart().createStudy('Moving Average', false, false, [99], null, {
      'Plot.linewidth': 1,
      'Plot.color': theme.tradingViewStyle.ma3,
    });
  };

  _lineDefault = () => {
    const { theme } = this.props;
    this._tv.chart().removeAllStudies();
    this._tv.chart().createStudy('Volume', false, false, [99], null, null);
  };
  _showIndicatorsDialog = () => {
    this._tv && this._tv.chart().executeActionById('insertIndicator');
  };

  render() {
    const { hidden } = this.props;
    const showSpinner = !hidden && this.state.loading;
    return (
      <Fragment>
        <LoadingShade hidden={!showSpinner} />
        <TradingChartInject />
        <TradingViewChartContainer
          hidden={this.props.selectedView !== TRADINGVIEW_VIEW_NAME}
          fullScreen={this.props.fullScreen}
          selectedView={this.props.selectedView}
        >
          <div className="tradingview-widget-container">
            <div id={TV_CONTAINER_ID}></div>
          </div>
        </TradingViewChartContainer>
      </Fragment>
    );
  }
}
